import React, { useCallback, useState, useRef, useLayoutEffect } from "react"

import { CollapsableContainer } from "@ciq/shared/components/collapsable"
import { EntryPillSelector, EntrySelector, ToolTipPropType } from "@ciq/shared/components/modal"
import { PopUpPosition } from "@ciq/shared/components/tooltip"
import Divider from "@ciq/shared/components/divider"
import { TitleView } from "@ciq/shared/components/map/titleview"
import { AttributeItem } from "@ciq/shared/components/map/components"
import { formatNumber, formatString } from "@ciq/shared/utils/format"
import { ListView } from "@ciq/shared/components/list"
import { Button } from "@ciq/shared/components/buttons"
import { ReactComponent as GraphIcon } from "@ciq/shared/assets/icons/graph_icon.svg"
import { UtilizationGraphByDay, UtilizationGraphByHour } from "../graphs/utilization"
import { RightPaneDataProvider, useRightPaneData } from "./useRightPaneData"
import { EmptyView } from "../../../../components/views"
import { TruncatedList } from "@ciq/shared/components/truncated_list"
import { useAnalyticsContext } from "../../../../contexts/AnalyticsContext"

export function MapRightPane(props) {
  const { children } = props

  return (
    <RightPaneDataProvider>
      <div className="map-pane-right">
        <Header>{children}</Header>
        <Divider />
        <Details />
      </div>
    </RightPaneDataProvider>
  )
}

function Header(props) {
  const { children } = props

  return (
    <div className="map-pane-right__header">
      <span>Details</span>
      {children}
    </div>
  )
}

function Details() {
  const { zoneData } = useRightPaneData()

  if (!zoneData.isVisible) {
    return <EmptyView>Please select your Building or Zone to view zone details.</EmptyView>
  }

  const Attribute = ({ label, value, ...props }) => {
    const { contentClassName, children, ...rest } = props
    const hasChildren = typeof children !== "undefined"

    return (
      <AttributeItem
        label={label}
        variant="horizontal"
        wrapperClassName={contentClassName}
        {...rest}
      >
        {!hasChildren && <span>{formatString(value)}</span>}
        {children}
      </AttributeItem>
    )
  }

  const OperatingHours = ({ hours }) => {
    const hasOperatingHours = hours != null

    return (
      <Attribute contentClassName="map-pane-operating-hours" label="Operating Hours">
        {!hasOperatingHours && <span>--</span>}
        {hasOperatingHours && hours.map((hour, i) => <span key={i}>{hour.label}</span>)}
      </Attribute>
    )
  }

  return (
    <div className="map-pane-right__content">
      <ListView>
        <CollapsableContainer header="Zone Details" collapsible={false}>
          <TitleView resource={zoneData.zone} />
          <OperatingHours hours={zoneData.operatingHours} />
          <div className="map-pane-right__attribute-grid">
            <Attribute label="Primary Usage" value={zoneData.primaryUsage} />
            <Attribute label="Secondary Usage" value={zoneData.secondaryUsage} />
            <Attribute label="Space Code" value={zoneData.spaceCode} />
          </div>
          <div className="map-pane-right__attribute-layout">
            <Attribute label={zoneData.floorLabel} value={zoneData.floors} />
            <AttributeItem label={zoneData.roomLabel} variant="horizontal">
              <TruncatedList items={zoneData.rooms} placeholder="--" />
            </AttributeItem>
          </div>
          <div className="map-pane-right__attribute-grid">
            <AttributeItem label="Square Footage" variant="vertical">
              <span>{formatNumber(zoneData.squareFootage)}</span>
            </AttributeItem>
            <AttributeItem label="Capacity" variant="vertical">
              <span>{formatNumber(zoneData.capacity)}</span>
            </AttributeItem>
          </div>
        </CollapsableContainer>
        <SemesterView />
        <HourlyView />
      </ListView>
    </div>
  )
}

function SemesterView() {
  const { semesterData } = useRightPaneData()

  if (!semesterData.isSummaryVisible || !semesterData.displayData) {
    return
  }

  return (
    <>
      {semesterData.displayData.map(summary => (
        <CollapsableContainer key={summary.id} header={summary.title} collapsible={false}>
          <div className="map-pane-right__attribute-grid">
            {summary.attributes.map((attribute, i) => (
              <AttributeItem
                key={attribute.id}
                label={attribute.label}
                variant="split"
                tooltip={{
                  type: ToolTipPropType.Info,
                  view: attribute.description,
                  position: i % 2 === 0 ? PopUpPosition.Top : PopUpPosition.TopRight,
                }}
              >
                <span>{attribute.value}</span>
              </AttributeItem>
            ))}
          </div>
        </CollapsableContainer>
      ))}
    </>
  )
}

/**
 * React component that displays hourly chart data with expand/collapse functionality.
 * Automatically scrolls the chart into view when expanded using useLayoutEffect to ensure
 * the scroll happens after DOM updates but before browser paint.
 *
 * @returns {JSX.Element|undefined} The hourly view component (button when collapsed, chart when expanded) or undefined if data is not visible
 */
function HourlyView() {
  const { hourlyData, zoneData } = useRightPaneData()
  const [isExpanded, setExpanded] = useState(false)
  const containerRef = useRef(null)
  const { map } = useAnalyticsContext()

  // Auto-scroll chart into view when expanded
  useLayoutEffect(() => {
    if (isExpanded) {
      containerRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" })
    }
  }, [isExpanded])

  const handleExpand = useCallback(() => {
    setExpanded(true)
    if (zoneData.zone) {
      map.trackGraphOpened({
        zone_id: zoneData.zone.id,
        zone_name: zoneData.zone.name,
        zone_type: zoneData.zone.type,
      })
    }
  }, [zoneData.zone, map])

  const handleCollapse = useCallback(() => {
    setExpanded(false)
  }, [])

  if (!hourlyData.isSummaryVisible || !hourlyData.graphData) {
    return
  }

  // Show collapsed button when not expanded
  if (!isExpanded) {
    return (
      <Button variant="primary" onClick={handleExpand}>
        <GraphIcon />
        View Hourly Chart
      </Button>
    )
  }

  // Wrap pill onChange to add tracking
  const pillWithTracking = {
    ...hourlyData.graphData.pill,
    onChange: selected => {
      hourlyData.graphData.pill.onChange(selected)
      map.trackGraphTypeChanged({
        graph_type: selected.value,
      })
    },
  }

  // Wrap selector onChange to add tracking
  const selectorWithTracking = {
    ...hourlyData.selector,
    onChange: selected => {
      hourlyData.selector.onChange(selected)
      map.trackMetricChanged({
        metric_id: selected.value,
        metric_name: selected.label,
        dataset: "hourly",
      })
    },
  }

  // Show expanded content wrapped in CollapsableContainer with close button
  return (
    <div ref={containerRef}>
      <CollapsableContainer header="Hourly Chart" onClose={handleCollapse}>
        <EntrySelector show={hourlyData.graphData.hasData} {...selectorWithTracking} />
        <EntryPillSelector
          className="map-pane-right__hourly-graph-type-selector"
          show={hourlyData.graphData.hasData}
          {...pillWithTracking}
        />
        <UtilizationGraphByDay
          data={hourlyData.graphData.graph.dayGraph}
          legend={hourlyData.graphData.graph.legend}
        />
        <UtilizationGraphByHour
          data={hourlyData.graphData.graph.hourGraph}
          legend={hourlyData.graphData.graph.legend}
        />
      </CollapsableContainer>
    </div>
  )
}
